import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { IconRail } from '@/features/reader/IconRail';
import { ApiError } from '@/lib/http';
import {
  fetchAdminKhutbahs,
  fetchJobs,
  fetchOverview,
  type JobRow,
  type KhutbahRow,
  type Overview,
} from './adminApi';

/**
 * The admin console.
 *
 * Its job is to answer **is anything wrong?** in one screen and otherwise
 * stay out of the way. Not an operations centre — one person, on a Thursday
 * evening, checking that this week is ready.
 *
 * The counts are honest about what cannot be known yet. The faithfulness
 * check never runs, so "unchecked" is every translation in the system;
 * reporting that as a problem would make the console permanently alarming
 * and teach an editor to ignore it. It says the check is not built instead.
 */

type Section = 'overview' | 'khutbahs' | 'jobs';

export function AdminConsole() {
  const [section, setSection] = useState<Section>('overview');

  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: ({ signal }) => fetchOverview(signal),
  });

  return (
    <div className="flex min-h-full bg-background text-foreground">
      <IconRail current="admin" />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-border bg-card px-4 py-3 sm:px-6">
          <div className="mx-auto w-full max-w-[1100px]">
            <h1 className="text-xl font-semibold">Admin</h1>
            <nav className="mt-3 flex flex-wrap gap-1">
              {(
                [
                  ['overview', 'Overview'],
                  ['khutbahs', 'Khutbahs'],
                  ['jobs', 'Jobs'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setSection(id)}
                  aria-current={section === id ? 'page' : undefined}
                  className={cn(
                    'min-h-9 rounded-md px-3 text-sm',
                    section === id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted',
                  )}
                >
                  {label}
                </button>
              ))}
              <span className="ms-auto flex flex-wrap gap-1">
                <Button type="button" variant="outline" size="lg" asChild>
                  <a href="?screen=upload">Upload</a>
                </Button>
                <Button type="button" variant="outline" size="lg" asChild>
                  <a href="?screen=review">Review queue</a>
                </Button>
                <Button type="button" variant="outline" size="lg" asChild>
                  <a href="?screen=glossary">Glossary</a>
                </Button>
              </span>
            </nav>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1100px] flex-1 px-4 py-6 sm:px-6">
          {section === 'overview' ? (
            overview.isPending ? (
              <Skeleton className="h-40" />
            ) : overview.error ? (
              <Failure error={overview.error} />
            ) : (
              <OverviewPanel overview={overview.data} />
            )
          ) : section === 'khutbahs' ? (
            <Khutbahs />
          ) : (
            <Jobs />
          )}
        </main>
      </div>
    </div>
  );
}

function OverviewPanel({ overview }: { overview: Overview }) {
  const problems = overview.failed + overview.deadJobs;

  return (
    <div className="flex flex-col gap-4">
      {/* The one sentence the screen exists for. */}
      <p
        className={cn(
          'rounded-lg border p-4 text-sm',
          problems > 0
            ? 'border-destructive font-medium'
            : 'border-border text-muted-foreground',
        )}
      >
        {problems > 0
          ? `${problems} thing${problems === 1 ? '' : 's'} need attention: ${
              overview.failed > 0 ? `${overview.failed} khutbah(s) failed` : ''
            }${overview.failed > 0 && overview.deadJobs > 0 ? ', ' : ''}${
              overview.deadJobs > 0 ? `${overview.deadJobs} dead job(s)` : ''
            }.`
          : 'Nothing is failing.'}
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Khutbahs" value={overview.khutbahs} sub={`${overview.published} published`} />
        <Stat
          label="Live and unreviewed"
          value={overview.liveUnreviewed}
          sub="readers can see these"
          warn={overview.liveUnreviewed > 0}
        />
        <Stat label="Processing" value={overview.processing} sub="in the pipeline" />
        <Stat
          label="Jobs running"
          value={overview.runningJobs}
          sub={`${overview.deadJobs} dead`}
          warn={overview.deadJobs > 0}
        />
      </div>

      {!overview.faithfulnessCheckRuns ? (
        <p className="rounded-md bg-muted p-3 text-[13px] text-pretty">
          <b>The faithfulness check is not built.</b> Nothing compares a
          translation against its Arabic, so every translation in the system is
          unchecked. That is a missing feature, not a backlog — the console does
          not count it as a problem.
        </p>
      ) : null}

      <section className="rounded-lg border border-dashed border-border p-4">
        <h2 className="text-sm font-semibold">Not built yet</h2>
        <ul className="mt-2 flex list-disc flex-col gap-1 ps-5 text-[13px] text-muted-foreground">
          <li>
            <b>Connectors</b> — no publisher fetch is implemented; khutbahs
            arrive by upload only.
          </li>
          <li>
            <b>Users and roles</b> — authentication is deferred, so there is one
            shared editor account and nothing to administer.
          </li>
          <li>
            <b>Usage and cost</b> — the <code>ai_usage</code> table exists and
            nothing writes to it, because no AI call is made.
          </li>
        </ul>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  warn,
}: {
  label: string;
  value: number;
  sub: string;
  warn?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-2xl font-semibold tabular-nums',
          warn && value > 0 ? 'text-warning-foreground' : undefined,
        )}
      >
        {value}
      </p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}

function Khutbahs() {
  const [status, setStatus] = useState<string | null>(null);
  const khutbahs = useQuery({
    queryKey: ['admin', 'khutbahs', status],
    queryFn: ({ signal }) => fetchAdminKhutbahs(status, signal),
  });

  return (
    <div className="flex flex-col gap-3">
      <label className="flex w-fit items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">Status</span>
        <select
          value={status ?? ''}
          onChange={(event) => setStatus(event.target.value || null)}
          className="h-8 rounded-md border border-border bg-background px-2 text-[13px]"
        >
          <option value="">All</option>
          {['draft', 'processing', 'ready_for_review', 'published', 'unpublished', 'failed'].map(
            (option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ),
          )}
        </select>
      </label>

      {khutbahs.isPending ? (
        <Skeleton className="h-40" />
      ) : khutbahs.error ? (
        <Failure error={khutbahs.error} />
      ) : khutbahs.data.length === 0 ? (
        <Empty title="No khutbahs" body="Upload one to begin." />
      ) : (
        <ul className="flex flex-col gap-2">
          {khutbahs.data.map((row) => (
            <KhutbahListRow key={row.id} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
}

function KhutbahListRow({ row }: { row: KhutbahRow }) {
  return (
    <li className="grid gap-3 rounded-lg border border-border bg-card p-3 md:grid-cols-[1fr_auto_auto_auto]">
      <div className="min-w-0">
        {row.titleOriginal ? (
          <p lang="ar" dir="rtl" className="script-ar truncate text-base font-normal">
            {row.titleOriginal}
          </p>
        ) : (
          <p className="truncate text-sm italic text-muted-foreground">Untitled</p>
        )}
        <p className="mt-0.5 text-xs text-muted-foreground">
          {row.friday} · {row.passages} passages · {row.languages} language
          {row.languages === 1 ? '' : 's'}
          {row.reviewedLanguages > 0 ? `, ${row.reviewedLanguages} reviewed` : ''}
        </p>
      </div>

      <span
        className={cn(
          'inline-flex h-6 items-center self-start rounded-sm px-2 text-[12px] font-semibold',
          row.status === 'published'
            ? 'bg-success-bg text-success'
            : row.status === 'failed'
              ? 'bg-destructive/10 text-destructive'
              : 'bg-muted text-muted-foreground',
        )}
      >
        {row.status}
      </span>

      {/* Brief §5: parsed is not confirmed. Surfaced so a person agrees with
          the guess or corrects it, rather than it hardening into fact. */}
      {row.needsConfirmation.length > 0 ? (
        <span
          className="inline-flex h-6 items-center self-start rounded-sm bg-warning-bg px-2 text-[12px] font-semibold text-warning"
          title={`Guessed by extraction: ${row.needsConfirmation.join(', ')}`}
        >
          {row.needsConfirmation.length} to confirm
        </span>
      ) : (
        <span />
      )}

      <span className="flex gap-1.5">
        <Button type="button" variant="outline" size="sm" asChild>
          <a href={`?screen=translate&khutbah=${encodeURIComponent(row.id)}`}>Translate</a>
        </Button>
        <Button type="button" variant="ghost" size="sm" asChild>
          <a href={`?khutbah=${encodeURIComponent(row.id)}`}>Read</a>
        </Button>
      </span>
    </li>
  );
}

function Jobs() {
  const [status, setStatus] = useState<string | null>(null);
  const jobs = useQuery({
    queryKey: ['admin', 'jobs', status],
    queryFn: ({ signal }) => fetchJobs(status, signal),
    refetchInterval: 15_000,
  });

  return (
    <div className="flex flex-col gap-3">
      <label className="flex w-fit items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">Status</span>
        <select
          value={status ?? ''}
          onChange={(event) => setStatus(event.target.value || null)}
          className="h-8 rounded-md border border-border bg-background px-2 text-[13px]"
        >
          <option value="">All</option>
          {['queued', 'leased', 'succeeded', 'failed', 'dead'].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>

      {jobs.isPending ? (
        <Skeleton className="h-40" />
      ) : jobs.error ? (
        <Failure error={jobs.error} />
      ) : jobs.data.length === 0 ? (
        <Empty
          title="No jobs"
          body="The pipeline runs translation, the faithfulness check and learning content — none of which is built, so nothing has been queued."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {jobs.data.map((job) => (
            <JobListRow key={job.id} job={job} />
          ))}
        </ul>
      )}
    </div>
  );
}

function JobListRow({ job }: { job: JobRow }) {
  const dead = job.status === 'dead';
  return (
    <li
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3',
        dead ? 'border-destructive' : 'border-border',
      )}
    >
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium">
          {job.type}
          {job.language ? ` · ${job.language}` : ''}
        </span>
        {job.lastError ? (
          <span className="truncate text-xs text-muted-foreground">{job.lastError}</span>
        ) : null}
      </span>
      <span className="flex items-center gap-3 text-xs">
        <span className="tabular-nums text-muted-foreground">
          attempt {job.attempts} of {job.maxAttempts}
        </span>
        <span
          className={cn(
            'inline-flex h-6 items-center rounded-sm px-2 font-semibold',
            dead
              ? 'bg-destructive/10 text-destructive'
              : job.status === 'succeeded'
                ? 'bg-success-bg text-success'
                : 'bg-muted text-muted-foreground',
          )}
        >
          {job.status}
        </span>
      </span>
    </li>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-8 text-center">
      <p className="font-semibold">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-pretty text-muted-foreground">
        {body}
      </p>
    </div>
  );
}

function Failure({ error }: { error: unknown }) {
  const api = error instanceof ApiError ? error : null;
  return (
    <div role="alert" className="rounded-lg border border-destructive p-4">
      <p className="font-semibold">
        {api?.unauthenticated ? 'Sign in to the editor surface first' : 'Could not load'}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {error instanceof Error ? error.message : 'Something went wrong.'}
      </p>
    </div>
  );
}
